import React, { useEffect, useRef, useState, useMemo } from 'react';

interface InstallationPreviewProps {
    widthMm: number;
    heightMm: number;
    imageUrl?: string;
    frameType?: string;
}

export const InstallationPreview: React.FC<InstallationPreviewProps> = ({
    widthMm,
    heightMm,
    imageUrl,
    frameType
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [images, setImages] = useState<Record<string, HTMLImageElement>>({});
    const [loaded, setLoaded] = useState(false);
    const [artworkImage, setArtworkImage] = useState<HTMLImageElement | null>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    // Constants
    const FRAME_WIDTH_PX = 27;
    const DPI = 72;
    const PIXELS_PER_MM = DPI / 25.4;

    const hasFrame = !!frameType && frameType !== 'none';
    const FRAME_OVERLAP_MM = hasFrame ? 9 : 0;
    const TOTAL_OVERLAP_MM = FRAME_OVERLAP_MM * 2;

    // Load artwork image
    useEffect(() => {
        if (!imageUrl) {
            setArtworkImage(null);
            return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setArtworkImage(img);
        img.src = imageUrl;
    }, [imageUrl]);

    // Load simulator assets
    useEffect(() => {
        const loadImages = async () => {
            const imagePaths = {
                cornerTopLeft: '/assets/simulator/aluminum_black_corner_top_left.png',
                cornerTopRight: '/assets/simulator/aluminum_black_corner_top_right.png',
                edgeTop: '/assets/simulator/aluminum_black_edge_top.png',
                edgeBottom: '/assets/simulator/aluminum_black_edge_bottom.png',
                edgeLeft: '/assets/simulator/aluminum_black_edge_left.png',
                edgeRight: '/assets/simulator/aluminum_black_edge_right.png',
                background: '/assets/simulator/room_background.jpg',
                people: '/assets/simulator/people.png'
            };

            const loadedImages: Record<string, HTMLImageElement> = {};
            const promises = Object.entries(imagePaths).map(([key, src]) => {
                return new Promise<void>((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        loadedImages[key] = img;
                        resolve();
                    };
                    img.onerror = () => {
                        console.warn(`Failed to load image: ${src}`);
                        resolve();
                    };
                    img.src = src;
                });
            });

            await Promise.all(promises);
            setImages(loadedImages);
            setLoaded(true);
        };

        loadImages();
    }, []);

    // Monitor container size
    useEffect(() => {
        if (!containerRef.current) return;
        const updateSize = () => {
            if (containerRef.current) {
                setContainerSize({
                    width: containerRef.current.clientWidth,
                    height: containerRef.current.clientHeight
                });
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Layout Calculation for Installation Mode
    const layout = useMemo(() => {
        if (containerSize.width === 0 || containerSize.height === 0) return null;

        const B1_WIDTH_MM = 728;
        const B1_HEIGHT_MM = 1030;
        const B1_REDUCTION = 0.5;

        const paperWidthMm = widthMm;
        const paperHeightMm = heightMm;

        const frameWidthMm = hasFrame ? (FRAME_WIDTH_PX / PIXELS_PER_MM) : 0;
        const innerWidthMm = paperWidthMm - TOTAL_OVERLAP_MM;
        const innerHeightMm = paperHeightMm - TOTAL_OVERLAP_MM;

        const outerWidthMm = innerWidthMm + (frameWidthMm * 2);
        const outerHeightMm = innerHeightMm + (frameWidthMm * 2);

        const A3_AREA = 297 * 420;
        const currentArea = outerWidthMm * outerHeightMm;
        const isSmallerThanA3 = currentArea <= A3_AREA;

        const backgroundZoom = isSmallerThanA3 ? 1.2 : 1.0;

        const B1_WIDTH_PX = B1_WIDTH_MM * PIXELS_PER_MM;
        const B1_HEIGHT_PX = B1_HEIGHT_MM * PIXELS_PER_MM;

        // Calculate background display size to position elements relative to the image
        let bgDisplayHeight = containerSize.height;
        if (images.background) {
            const bgAspect = images.background.width / images.background.height;
            // Assuming 'contain' and width-constrained or 'cover' (if zoom)
            // But we want to align with the visual 'top' of the image.

            // If isSmallerThanA3 (cover/zoom), layout is tricky, but let's base on container width for consistency if responsive.
            // If standard (contain), width usually fits container width.
            const projectedHeight = containerSize.width / bgAspect;
            if (projectedHeight < containerSize.height) {
                // Width fits, height is smaller than container
                bgDisplayHeight = projectedHeight;
            } else {
                // Height fits, width is smaller (side bars) - but with top-center alignment?
                // Using 'top center', the image starts at 0.
                bgDisplayHeight = containerSize.height;
            }
        }

        const B1_fitScaleX = containerSize.width / B1_WIDTH_PX;
        const B1_fitScaleY = containerSize.height / B1_HEIGHT_PX;
        const B1_baseScale = Math.min(B1_fitScaleX, B1_fitScaleY, 1);
        const B1_finalScale = B1_baseScale * B1_REDUCTION;

        let scale = B1_finalScale * backgroundZoom;
        if (isSmallerThanA3) {
            scale = scale * 0.7;
        }

        // Use bgDisplayHeight for Y positioning to keep relative to image top
        const targetCenterY = bgDisplayHeight * (isSmallerThanA3 ? 0.35 : 0.45);
        const targetCenterX = containerSize.width * 0.5;

        const currentOuterWidthPx = outerWidthMm * PIXELS_PER_MM * scale;
        const currentOuterHeightPx = outerHeightMm * PIXELS_PER_MM * scale;

        const canvasTop = targetCenterY - (currentOuterHeightPx / 2);
        const canvasLeft = targetCenterX - (currentOuterWidthPx / 2);

        return {
            scale,
            canvasTop,
            canvasLeft,
            outerWidthMm,
            outerHeightMm,
            innerWidthMm,
            innerHeightMm,
            isSmallerThanA3,
            backgroundZoom
        };
    }, [widthMm, heightMm, containerSize, hasFrame, FRAME_OVERLAP_MM, TOTAL_OVERLAP_MM, images]);

    // Canvas Rendering
    useEffect(() => {
        if (!loaded || !canvasRef.current || !layout) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { scale, outerWidthMm, outerHeightMm } = layout;

        const dpr = window.devicePixelRatio || 1;
        const finalWidth = outerWidthMm * PIXELS_PER_MM * scale;
        const finalHeight = outerHeightMm * PIXELS_PER_MM * scale;

        canvas.width = finalWidth * dpr;
        canvas.height = finalHeight * dpr;
        canvas.style.width = `${finalWidth}px`;
        canvas.style.height = `${finalHeight}px`;
        canvas.style.position = 'absolute';
        canvas.style.top = `${layout.canvasTop}px`;
        canvas.style.left = `${layout.canvasLeft}px`;

        ctx.scale(dpr, dpr);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, finalWidth, finalHeight);

        // Draw Artwork
        if (artworkImage) {
            const frameWidthPxScaled = hasFrame ? (FRAME_WIDTH_PX * scale) : 0;
            const overlapPxScaled = hasFrame ? (FRAME_OVERLAP_MM * PIXELS_PER_MM * scale) : 0;

            const paperX = frameWidthPxScaled - overlapPxScaled;
            const paperY = frameWidthPxScaled - overlapPxScaled;
            const paperW = widthMm * PIXELS_PER_MM * scale;
            const paperH = heightMm * PIXELS_PER_MM * scale;

            ctx.fillStyle = '#ffffff';
            ctx.fillRect(paperX, paperY, paperW, paperH);

            const imgAspect = artworkImage.width / artworkImage.height;
            const paperAspect = paperW / paperH;

            let drawW, drawH, drawX, drawY;

            if (imgAspect > paperAspect) {
                drawW = paperW;
                drawH = paperW / imgAspect;
                drawX = paperX;
                drawY = paperY + (paperH - drawH) / 2;
            } else {
                drawH = paperH;
                drawW = paperH * imgAspect;
                drawY = paperY;
                drawX = paperX + (paperW - drawW) / 2;
            }

            ctx.save();
            ctx.beginPath();
            ctx.rect(paperX, paperY, paperW, paperH);
            ctx.clip();
            ctx.drawImage(artworkImage, drawX, drawY, drawW, drawH);
            ctx.restore();
        }

        // Draw Frame
        if (hasFrame) {
            const scaledCornerSize = Math.max(images.cornerTopLeft?.width || 0, images.cornerTopLeft?.height || 0) * scale * 0.5;
            const scaledFrameWidth = FRAME_WIDTH_PX * scale;

            const drawEdge = (side: 'top' | 'bottom' | 'left' | 'right', x: number, y: number, w: number, h: number) => {
                let img;
                switch (side) {
                    case 'top': img = images.edgeTop; break;
                    case 'bottom': img = images.edgeBottom; break;
                    case 'left': img = images.edgeLeft; break;
                    case 'right': img = images.edgeRight; break;
                }
                if (!img) return;

                if (side === 'top' || side === 'bottom') {
                    ctx.drawImage(img, 0, 0, img.width, img.height, x, y, w, scaledFrameWidth);
                } else {
                    ctx.drawImage(img, 0, 0, img.width, img.height, x, y, scaledFrameWidth, h);
                }
            };

            const drawCorner = (pos: 'tl' | 'tr' | 'bl' | 'br', x: number, y: number) => {
                let img;
                let rotation = 0;
                switch (pos) {
                    case 'tl': img = images.cornerTopLeft; break;
                    case 'tr': img = images.cornerTopRight; break;
                    case 'bl': img = images.cornerTopRight; rotation = 180; break;
                    case 'br': img = images.cornerTopLeft; rotation = 180; break;
                }
                if (!img) return;

                const size = scaledCornerSize;
                ctx.save();
                const cx = x + size / 2;
                const cy = y + size / 2;
                ctx.translate(cx, cy);
                ctx.rotate(rotation * Math.PI / 180);
                ctx.translate(-cx, -cy);
                ctx.drawImage(img, x, y, size, size);
                ctx.restore();
            };

            const topEdgeWidth = finalWidth - scaledCornerSize * 2;
            if (topEdgeWidth > 0) drawEdge('top', scaledCornerSize, 0, topEdgeWidth, scaledFrameWidth);

            const bottomEdgeWidth = finalWidth - scaledCornerSize * 2;
            if (bottomEdgeWidth > 0) drawEdge('bottom', scaledCornerSize, finalHeight - scaledFrameWidth, bottomEdgeWidth, scaledFrameWidth);

            const leftEdgeHeight = finalHeight - scaledCornerSize * 2;
            if (leftEdgeHeight > 0) drawEdge('left', 0, scaledCornerSize, scaledFrameWidth, leftEdgeHeight);

            const rightEdgeHeight = finalHeight - scaledCornerSize * 2;
            if (rightEdgeHeight > 0) drawEdge('right', finalWidth - scaledFrameWidth, scaledCornerSize, scaledFrameWidth, rightEdgeHeight);

            drawCorner('tl', 0, 0);
            drawCorner('tr', finalWidth - scaledCornerSize, 0);
            drawCorner('br', finalWidth - scaledCornerSize, finalHeight - scaledCornerSize);
            drawCorner('bl', 0, finalHeight - scaledCornerSize);
        }

    }, [loaded, layout, artworkImage, hasFrame, images, widthMm, heightMm, FRAME_OVERLAP_MM, PIXELS_PER_MM, FRAME_WIDTH_PX]);

    // Render Overlays
    const renderOverlays = () => {
        if (!layout) return null;

        const { isSmallerThanA3, canvasTop, canvasLeft, outerWidthMm, outerHeightMm, scale } = layout;

        const personStyle: React.CSSProperties = {
            position: 'absolute',
            left: '70%',
            bottom: isSmallerThanA3 ? '30px' : '50px',
            height: `${1700 * PIXELS_PER_MM * (scale / layout.backgroundZoom)}px`,
            objectFit: 'contain',
            pointerEvents: 'none',
            opacity: 0.9,
            zIndex: 10
        };

        const frameWidthPx = outerWidthMm * PIXELS_PER_MM * scale;
        const frameHeightPx = outerHeightMm * PIXELS_PER_MM * scale;

        const distFromFrame = 20;
        const textOffset = 50;

        return (
            <>
                {images.people && (
                    <img
                        src={images.people.src}
                        alt="Person (170cm)"
                        style={personStyle}
                    />
                )}

                {/* Dimensions - Left (Height) */}
                <div style={{
                    position: 'absolute',
                    top: canvasTop,
                    left: canvasLeft - distFromFrame,
                    height: frameHeightPx,
                    width: '1px',
                    backgroundColor: 'black'
                }}>
                    <div style={{ position: 'absolute', top: 0, left: '-5px', width: '10px', height: '1px', backgroundColor: 'black' }} />
                    <div style={{ position: 'absolute', bottom: 0, left: '-5px', width: '10px', height: '1px', backgroundColor: 'black' }} />
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        right: `${textOffset}px`,
                        transform: 'translateY(-50%)',
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        {Math.round(outerHeightMm)} mm
                    </div>
                </div>

                {/* Dimensions - Bottom (Width) */}
                <div style={{
                    position: 'absolute',
                    top: canvasTop + frameHeightPx + distFromFrame,
                    left: canvasLeft,
                    width: frameWidthPx,
                    height: '1px',
                    backgroundColor: 'black'
                }}>
                    <div style={{ position: 'absolute', left: 0, top: '-5px', width: '1px', height: '10px', backgroundColor: 'black' }} />
                    <div style={{ position: 'absolute', right: 0, top: '-5px', width: '1px', height: '10px', backgroundColor: 'black' }} />
                    <div style={{
                        position: 'absolute',
                        left: '50%',
                        top: `${textOffset}px`,
                        transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        {Math.round(outerWidthMm)} mm
                    </div>
                </div>
            </>
        );
    };

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full overflow-hidden flex items-center justify-center bg-gray-100"
            style={{
                backgroundImage: images.background ? `url('${images.background.src}')` : 'none',
                backgroundSize: layout?.isSmallerThanA3 ? 'cover' : 'contain',
                backgroundPosition: 'top center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <canvas ref={canvasRef} />
            {renderOverlays()}
        </div>
    );
};
